import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";

import { api_without_cancellation } from "@/api/api";
import "./tags-search.css";

const norm = (t) => (typeof t === "string" ? t : (t?.name ?? "")).trim();
const eq = (a, b) => a.toLowerCase() === b.toLowerCase();

function useLibraryTags() {
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = () => {
    setIsLoading(true);
    setError(null);
    api_without_cancellation
      .get("/api/document-library/tags/")
      .then((res) => {
        const data = res.data ?? [];
        setTags(data.map(norm).filter(Boolean));
      })
      .catch((e) => {
        setError(e);
        setTags([]);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(load, []);
  return { tags, isLoading, error, reloadTags: load };
}

const TagSearch = ({ documentId, documentTags = [], onTagsChange }) => {
  const { tags: libraryTags, isLoading, reloadTags } = useLibraryTags();

  const [docTags, setDocTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debounced] = useDebounce(searchTerm, 200);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isFocused, setIsFocused] = useState(false);

  // Reseed only when the document changes, not on every parent render.
  useEffect(() => {
    setDocTags((documentTags ?? []).map(norm).filter(Boolean));
    setSearchTerm("");
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  const query = debounced.trim();
  const rawQuery = searchTerm.trim();

  const suggestions = useMemo(
    () => libraryTags.filter((t) => t.toLowerCase().includes(query.toLowerCase())),
    [libraryTags, query]
  );

  const existsInLibrary = useMemo(
    () => libraryTags.some((t) => eq(t, rawQuery)),
    [libraryTags, rawQuery]
  );
  const onThisDoc = (tag) => docTags.some((t) => eq(t, tag));

  const showAdd = rawQuery.length > 0 && !existsInLibrary && !saving;

  const persist = async (nextTags) => {
    setSaving(true);
    setError(null);
    try {
      await api_without_cancellation.patch(`/api/document-library/documents/${documentId}/`, {
        tags: nextTags,
      });
      setDocTags(nextTags);
      onTagsChange?.(nextTags);
      reloadTags();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  const addTag = (tag) => {
    const clean = norm(tag);
    if (!clean || onThisDoc(clean)) return;
    persist([...docTags, clean]);
  };

  const removeTag = (tag) => {
    const clean = norm(tag);
    persist(docTags.filter((t) => !eq(t, clean)));
  };

  return (
    <div
      className="tag-search-container"
      style={{
        background: "var(--primary-20)",
        padding: "5px",
      }}
    >
      <div className="tag-search-row d-flex align-items-center" style={{ gap: "5px" }}>
        <div className="tag-search-input-wrap" style={{ position: "relative", flex: 1 }}>
          <input
            type="text"
            className="form-control p-l-5 document-search-text-color"
            id="tag-search-input"
            placeholder="Search Tags — type to filter, add a new tag, or remove one on this document"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setTimeout(() => setIsFocused(false), 120)}
            style={{
              height: "25px",
              padding: "5px 10px",
              fontWeight: "600",
              borderRadius: "0px",
              color: isFocused ? "black" : "var(--primary-25)",
            }}
          />
          {isFocused && rawQuery.length > 0 && (
            <div className="tag-search-dropdown">
              {suggestions.map((tag, i) => {
                const attached = onThisDoc(tag);
                return (
                  <div
                    key={tag}
                    className="tag-search-option d-flex align-items-center justify-content-between"
                    style={{ background: i % 2 === 0 ? "var(--primary-4)" : "var(--primary-2)" }}
                  >
                    <span style={{ fontWeight: "600", fontSize: "14px", color: "#000" }}>
                      #{tag}
                    </span>
                    <button
                      type="button"
                      className={`${attached ? "btn-danger" : "btn-success"} height-25 tag-search-action-btn`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => (attached ? removeTag(tag) : addTag(tag))}
                      disabled={saving}
                    >
                      {attached ? "Remove from Document" : "Add to Document"}
                    </button>
                  </div>
                );
              })}

              {!isLoading && showAdd && (
                <div
                  className="tag-search-option d-flex align-items-center justify-content-between"
                  style={{
                    background:
                      suggestions.length % 2 === 0 ? "var(--primary-4)" : "var(--primary-2)",
                  }}
                >
                  <span style={{ fontWeight: "600", fontSize: "14px", color: "var(--primary)" }}>
                    #{rawQuery}
                  </span>
                  <button
                    type="button"
                    className="btn-success height-25 tag-search-action-btn"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => addTag(rawQuery)}
                    disabled={saving}
                  >
                    Add New Tag to Library
                  </button>
                </div>
              )}

              {!isLoading && !showAdd && suggestions.length === 0 && (
                <div className="tag-search-empty text-center">No matching tags</div>
              )}
            </div>
          )}{" "}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger m-t-5" style={{ marginBottom: 0 }}>
          Couldn’t update tags: {error?.response?.data?.detail || error.message}
        </div>
      )}
    </div>
  );
};

export default TagSearch;
